import { Injectable } from '@angular/core';
import { FormGroup, FormArray, FormControl, ValidatorFn } from '@angular/forms';
import { merge, difference } from 'lodash';
import { ValidationContext } from './validation-context';
import { BehaviorSubject } from 'rxjs';
import { SchemasService } from './schemas.service';
import { ViewsService } from './views.service';
import { BroadcastService } from '@cv-invision/core';
import { LoggerService } from '@cv-invision/core';
import { FORM_EVENT } from '../models/enums.model';

@Injectable({
	providedIn: 'root'
})
export class FormService {

	formObserver = new BehaviorSubject(undefined);
	submitObserver = new BehaviorSubject(false);

	constructor(
		private broadcastService: BroadcastService,
		private schemasS: SchemasService,
		private viewsServices: ViewsService,
		private console: LoggerService
	) {
		//
	}

	form: FormGroup;
	// order: any;
	// schemas: any = {};
	// helperText: any = {};

	get order(): any {
		return this.viewsServices.order;
	}
	get document(): any {
		return this.viewsServices.document;
	}
	get photos(): any {
		return this.viewsServices.photos;
	}

	get canEditOrder(): boolean {
		const status = this.order.inspection.status.toLowerCase();
		return status === 'invalid' || status === 'incomplete';
	}

	/**
	 * initial point
	 * ToDo:
	 * - Add default initial pointer depending on the schema. Example: InspectionReport
	 */
	async initSchemas(): Promise<void> {
		// this.order = order;
		if (this.order.id) {
			const raw = merge(
				this.toData(this.schemasS.schemas),
				this.omit(this.document, (key, value) => value !== null)
			);

			const allLevels = (this.schemasS.query(this.document, `$..levels`) || []);

			// Get all Room where position is Exterior
			const allExterior = (this.schemasS.query(allLevels, `$..rooms[?(@.position=="Exterior" && @.isOutsideArea==true)]`) || [])
				.filter(room => 'roomName' in room && room.roomName !== null && room.roomName !== '');
			// Get all Room where position is Interior
			const allInterior = (this.schemasS.query(allLevels, `$..rooms[?(@.position=="Interior" && @.isOutsideArea==false)]`) || [])
				.filter(room => 'roomName' in room && room.roomName !== null && room.roomName !== '');

			this.viewsServices.setRooms({interior: allInterior, exterior: allExterior});
			this.viewsServices.setLevels(allLevels);

			// this.addPhotoControls(raw);
			this.form = undefined;
			this.form = this.toForm(raw, []) as FormGroup;
			// this.initPhotos(order.inspection.photos);
			this.markAsTouched(this.form);
			this.addMissingProperties(this.form, []);
			this.validate(this.form);
			// this.update();
			this.viewsServices.setForm(this.form);
			// this.console.log(this.form);
			this.formObserver.next(this.form);

			// For DEMO Propuses ONLY | DONT DELETE, its helpfull on DEVELOP env
			// const gps = this.form.get('InspectionReport').get('property').get('identification');
			// const cvGps = gps.get('cv_gpsCoordinates');
			// const orGps = gps.get('gpsCoordinates');
			// if (cvGps.value.latitude === '') {
			// 	cvGps.get('latitude').setValue(orGps.value.latitude);
			// 	cvGps.get('longitude').setValue(orGps.value.longitude);
			// }
			// For DEMO Propuses ONLY
		}
	}

	/**
	 * adds missing properties to the form builder
	 * @param c
	 * @param path
	 */
	addMissingProperties(c: FormGroup, path: any[]): void {
		if (c instanceof FormGroup) {
			const propertyPath = `${path.filter(p => /^\d+$/.test(p) === false).join('.')}`;
			// console.log(propertyPath);
			const current = Object.keys(c.controls);
			const desired = Object.keys(this.schemasS.getPropertiesByPath(propertyPath));
			const i = desired.findIndex(propertyName => propertyName === 'Metadata');

			if (i !== -1) {
				desired.splice(i, 1);
			}

			difference(desired, current).forEach( (propertyName: any) => {
				if (propertyName !== 'cv_photos') {
					const metadata = this.schemasS.getMetadataByPath(`${propertyPath}.${propertyName}`);
					if (metadata && Object.prototype.hasOwnProperty.call(metadata, 'MinOccurs')) {
						c.setControl(propertyName, new FormArray([], this.requirednessValidator(metadata)));
					} else if (metadata && Object.prototype.hasOwnProperty.call(metadata, 'DataType')) {
						c.setControl(propertyName, new FormControl('', this.requirednessValidator(metadata)));
					} else {
						c.setControl(propertyName, new FormGroup({}, this.requirednessValidator(metadata)));
					}

					if (!metadata) {
						this.console.warn(`Value for: ${propertyPath}.${propertyName} not found in ` + this.schemasS['sectionsReview']['schema']);
					}
				}
			});
		}

		// tslint:disable-next-line: forin
		for (const key in c.controls) {
			const control = c.get(key) as FormGroup;
			if (control.controls) {
				this.addMissingProperties(control, [...path, key]);
			}
		}

	}

	/**
	 * Recursive function
	 * transforms json object into FormBuilder
	 * @param obj
	 * @param path
	 */
	toForm(obj: any, path: any[]): any {
		const metadata = this.schemasS.getMetadataByPath(
			`${path.filter(p => /^\d+$/.test(p) === false).join('.')}`
		);

		const result: any = obj.constructor.name !== 'Array' ?
			new FormGroup({}, metadata ? this.requirednessValidator(metadata) : null) :
			new FormArray([], metadata ? this.requirednessValidator(metadata) : null);

		for (const key in obj) {
			if (obj[key] !== null && typeof obj[key] === 'object') {
				result.setControl(key, this.toForm(obj[key], [...path, `${key}`]));
			} else {
				result.setControl(
					key,
					new FormControl(
						obj[key],
						this.requirednessValidator(
							this.schemasS.getMetadataByPath(
								`${[...path, key].filter(p => /^\d+$/.test(p) === false).join('.')}`
							)
						)
					)
				);
			}
		}
		return result;
	}

	/**
	 * Recursive function
	 * transforms schema into FormBuilder base
	 * @param obj
	 */
	public toData(obj: any): any {
		const result = obj.constructor.name === 'Array' ? [] : {};
		for (const key in obj) {
			if (key === 'Metadata') {
				continue;
			}

			let newKey = key;
			if (['InspectionReport', 'CV_EXTENSION_SECTION'].indexOf(key) === -1) {
				newKey = `${key.charAt(0).toLocaleLowerCase()}${key.slice(1)}`;
			}

			if (obj[key]['Metadata'] !== undefined) {
				let value: any;
				switch (obj[key]['Metadata'][0]['DataType']) {
					case 'String': case 'Enumerated': value = null; break;
					case 'Numeric': case 'Integer': value = null; break;
					case 'Boolean': value = null; break;
					case 'Date': value = null; break;
					default: value = obj[key]['Metadata'][0]['MinOccurs'] !== undefined ? [] : this.toData(obj[key]); break;
				}

				result[newKey] = this.specialCasesValues(obj[key], value);
			} else {
				result[newKey] = this.toData(obj[key]);
			}
		}
		return result;
	}

	/**
	 * Return the validation function from the schema given
	 *
	 * @param schema
	 */
	requirednessValidator(schema: any): ValidatorFn {
		return control => {
			if (this.form === undefined || schema === undefined || control.root instanceof FormControl) {
				return null;
			}

			let message = schema.Name ? `${schema.Name} is required` : '';
			let isInvalid = false;

			if (control instanceof FormArray && (control as FormArray).controls.filter(c => c.get('photoType')).length !== 0) {
				const rejectedPhotos = (control as FormArray).controls.filter(c => c.get('status') && c.get('status').value === 'REJECTED');
				if (rejectedPhotos.length !== 0) {
					return { required: rejectedPhotos.map(c => c.get('photoType').value).join(',') };
				}
			}

			if (schema.Required !== undefined && schema.Required === 'True') {
				isInvalid = control instanceof FormArray ?
					(control as FormArray).controls.length === 0 :
					(control.value === null || control.value.toString().trim() === '');
			} else if (schema.Conditionally !== undefined) {
				// tslint:disable-next-line:no-eval
				const evaluation: any = eval(`(${schema.Conditionally})`)({}, new ValidationContext(control, this.schemasS));
				if (typeof evaluation === 'object') {
					message = evaluation._label;
				}
				isInvalid = evaluation as boolean;
			}

			return isInvalid ? { required: message } : null;
		};
	}

	/**
	 * Recursive function
	 * Skip objects from the FromBuilder values
	 * @param obj
	 * @param callback
	 */
	public omit(obj: any, callback: (key: string | number, value: any) => boolean): any {
		const result = obj.constructor.name === 'Array' ? [] : {};
		for (const key in obj) {
			if (callback(key, obj[key]) && key !== 'cv_photos') {
				if (obj[key] && typeof obj[key] === 'object') {
					result[key] = this.omit(obj[key], callback);
				} else {
					result[key] = obj[key];
				}
			}
		}
		return result;
	}

	/**
	 * Recursive function
	 * Fires the validation of the FormBuilder
	 * @param c
	 */
	validate(c: any): void {
		// tslint:disable-next-line:forin
		for (const key in c.controls) {
			const control = c.get(key);
			control.updateValueAndValidity({ onlySelf: true });
			if (control instanceof FormGroup ||
				control instanceof FormArray) {
				this.validate(control);
			}
		}
		c.updateValueAndValidity();
	}

	/**
	 * Recursive Function
	 * Marks the control as touched, to fire the validation UI
	 * @param formGroup
	 */
	markAsTouched(formGroup: FormGroup): void {
		(Object as any).values(formGroup.controls).forEach(control => {
			control.markAsTouched();
			if (control.controls) {
				this.markAsTouched(control);
			}
		});
	}

	/**
	 * Special cases fields
	 * NOTE:
	 * This functions its going to serve when there is any field that require a special default value.
	 * @param data
	 */
	specialCasesValues(schemaItem: any, value: string): any {
		let newValue = value;
		switch (schemaItem['Metadata'][0]['name']) {
			case 'latitude':
			case 'longitude':
			case 'cv_latitude':
			case 'cv_longitude':
				newValue = null;
				break;
		}

		return newValue;
	}

	/**
	 * Patch the full form values
	 *
	 * @param newValues JSON document
	 */
	patchFormValues(newValues?: any): void {
		let values = newValues;
		if (!values) {
			values = this.document;
		}

		this.form.patchValue(values);
	}

	/**
	 * test
	 */
	async update(): Promise<any> {
		const document = this.form.getRawValue();
		this.viewsServices.setDocument(document);
		this.broadcastService.broadcast({
			name: FORM_EVENT.UPDATE,
			event: {
				data: document,
				formValid: this.form.valid
			}
		});
	}

	/**
	 * doc must exist
	 */
	async fillData(document: any): Promise<any> {
		const raw = merge(
			this.toData(this.schemasS.schemas),
			this.omit(document, (key, value) => value !== null)
		);
		const form = this.toForm(raw, []) as FormGroup;
		this.addMissingProperties(form, []);

		const newDocument = form.getRawValue();
		this.viewsServices.setDocument(newDocument);
		return newDocument;
	}
}
